/**
 * Feeds the viewer's active UI locale to the SDK client so every API request
 * carries an `x-locale` header, enabling on-demand translation of platform
 * content on the server (BL-202). Must be mounted inside both SdkProvider
 * (for `useApiClient` + the QueryClient) and LocaleProvider (for `useLocale`).
 * Renders nothing. One bridge for every app that talks to the API — Journey
 * Web, Business Web, Citizen Mobile and the embedded InOut calculator.
 *
 * EN is a no-op end-to-end — the SDK omits the header for `en`.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useLocale } from '@tonnie/locale';
import { useEffect, useRef } from 'react';

import { useApiClient } from './SdkProvider';

export function LocaleBridge(): null {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const { locale } = useLocale();
  // The locale under which requests have been going out (the value last handed
  // to the SDK provider). `null` = not captured yet.
  const registeredLocale = useRef<string | null>(null);

  // `client` is a stable SdkProvider singleton; the effect re-runs on locale
  // change to re-register the current locale.
  useEffect(() => {
    client.setLocaleProvider(() => locale);
    return () => client.setLocaleProvider(null);
  }, [client, locale]);

  // Invalidate every query when the active locale CHANGES from the one earlier
  // requests used, so server-translated (Tier-B) content — CheckUp prose, CoPilot
  // guide flows, brain configs (the motivational questions' concept/context) —
  // refetches under the new `x-locale`. This covers BOTH a user switch AND —
  // critically for native — the boot-time `en → <persisted>` settle:
  // `NativeLocaleProvider` resolves the persisted locale ASYNCHRONOUSLY from
  // AsyncStorage, so early requests (e.g. a dashboard prefetch) can go out under
  // the transient `en` default and cache an English result. Skipping that settle
  // (the prior behavior) left non-EN mobile users stuck with English content
  // until a manual language switch. Web settles too: `LocaleProvider` renders
  // first with i18n's current language and applies `initialLocale` (a portal's
  // language, a saved preference) in an effect — the embedded InOut calculator
  // fetched its first answer under `en` that way. The first observed locale is
  // captured as the baseline WITHOUT invalidating (nothing has been fetched under
  // another locale yet).
  useEffect(() => {
    if (registeredLocale.current === null) {
      registeredLocale.current = locale;
      return;
    }
    if (registeredLocale.current === locale) return;
    registeredLocale.current = locale;
    // Cancel first: a query still in flight under the old locale with no data
    // yet (a first load during the boot-time settle) is JOINED by an
    // invalidation, not refetched — and its old-locale answer would stay.
    // Cancelled, it reverts to pending and the invalidation fetches it anew,
    // under the new `x-locale`.
    void queryClient.cancelQueries().then(() => queryClient.invalidateQueries());
  }, [queryClient, locale]);

  return null;
}
